import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const ReplyTable = ({ replyData, onLoadMore, hasMore, isLoading }) => {
    const dataArray = useMemo(() => {
        return replyData?.data?.content ?? [];
    }, [replyData]);

    const [filters, setFilters] = useState({
        willAttend: '',
        willHaveMeal: '',
        weddingPartyRole: '',
    });

    const observerRef = useRef(null);
    const lastElementRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoading) {
                    onLoadMore();
                }
            },
            { threshold: 0.1 }
        );

        observerRef.current = observer;

        if (lastElementRef.current) {
            observer.observe(lastElementRef.current);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasMore, isLoading, onLoadMore]);

    const filteredData = useMemo(() => {
        return dataArray.filter((item) => {
            return (
                (filters.willAttend ? item.willAttend === filters.willAttend : true) &&
                (filters.willHaveMeal ? item.willHaveMeal === filters.willHaveMeal : true) &&
                (filters.weddingPartyRole ? item.weddingPartyRole === filters.weddingPartyRole : true)
            );
        });
    }, [dataArray, filters]);

    return (
        <div style={{ 
            width: '100%',
            borderRadius: '10px',
            overflow: 'hidden',
            border: '1px solid #313131',
            marginTop: '7px'
        }}>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '13px',
                    tableLayout: 'fixed',
                    minWidth: '500px'
                }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f8f8f8' }}>
                            <th style={{ padding: '10px', borderBottom: '1px solid #313131', width: '70px', textAlign: 'center'}}>이름</th>
                            <th style={{ padding: '10px', borderBottom: '1px solid #313131', width: '53px', textAlign: 'center' }}>참석측</th>
                            <th style={{ padding: '10px', borderBottom: '1px solid #313131', width: '43px',textAlign: 'center' }}>참석</th>
                            <th style={{ padding: '10px', borderBottom: '1px solid #313131', width: '43px',textAlign: 'center'  }}>식사</th>
                            <th style={{ padding: '10px', borderBottom: '1px solid #313131', width: '45px', textAlign: 'center' }}>인원</th>
                            <th style={{ padding: '10px', borderBottom: '1px solid #313131', width: '73px', textAlign: 'center' }}>답변일자</th>
                            <th style={{ padding: '10px', borderBottom: '1px solid #313131', width: '110px', textAlign: 'center' }}>휴대폰번호</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length > 0 ? (
                            filteredData.map((reply, index) => (
                                <tr 
                                    key={reply.id} 
                                    ref={index === filteredData.length - 1 ? lastElementRef : null}
                                    style={{ 
                                        borderBottom: index === filteredData.length - 1 ? 'none' : '1px solid #e0e0e0'
                                    }}
                                >
                                    <td style={{ 
                                        padding: '10px', 
                                        textAlign: 'center',
                                        overflow: 'hidden',
                                        overflowX: 'scroll',
                                        whiteSpace: 'nowrap'
                                    }}>{reply.name}</td>
                                    <td style={{ 
                                        padding: '10px',
                                        textAlign: 'center',
                                        color: reply.weddingPartyRole === 'BRIDE' ? '#FF8A83' : '#5a9ee8'
                                    }}>
                                        {reply.weddingPartyRole === 'BRIDE' ? '신부' : '신랑'}
                                    </td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>{reply.willAttend === 'Y' ? 'O' : 'X'}</td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>{reply.willHaveMeal === 'Y' ? 'O' : 'X'}</td>
                                    <td style={{ 
                                        padding: '10px', 
                                        textAlign: 'center',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{reply.companionCount}</td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>
                                        {(() => {
                                            const date = new Date(reply.createdAt);
                                            const day = String(date.getDate()).padStart(2, '0');
                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                            const year = String(date.getFullYear()).slice(-2);
                                            return `${year}.${month}.${day}`;
                                        })()}
                                    </td>
                                    <td style={{ 
                                        padding: '10px', 
                                        textAlign: 'center',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{reply.phoneNumber || '-'}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" style={{ padding: '20px', textAlign: 'center' }}>데이터가 없습니다</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {isLoading && (
                <div style={{ 
                    padding: '20px', 
                    textAlign: 'center',
                    backgroundColor: '#f8f8f8'
                }}>
                    로딩 중...
                </div>
            )}
        </div>
    );
};

ReplyTable.propTypes = {
    replyData: PropTypes.shape({
        data: PropTypes.shape({
            content: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.number.isRequired,
                name: PropTypes.string.isRequired,
                willAttend: PropTypes.string.isRequired,
                willHaveMeal: PropTypes.string.isRequired,
                weddingPartyRole: PropTypes.string.isRequired,
                companionCount: PropTypes.string.isRequired,
                createdAt: PropTypes.string.isRequired
            }))
        })
    }),
    onLoadMore: PropTypes.func.isRequired,
    hasMore: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired
};

export default ReplyTable;
